import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import Header from '../components/layout/Header';
import SideMenu from '../components/layout/SideMenu';
import Alert from '../components/common/Alert';
import ProductFormModal from '../components/products/ProductFormModal';
import ProductCard from '../components/products/ProductCard';
import SectorFilter from '../components/products/SectorFilter';
import ViewToggle from '../components/products/ViewToggle';
import { useStorePageGuard } from '../hooks/useStorePageGuard';
import { useAuth } from '../contexts/AuthContext';
import { Product, CreateProductPayload, UpdateProductPayload, ViewMode } from '../types/product';
import { Sector } from '../types/sector';
import { getProductsByStore, getProductsBySector, createProduct, updateProduct, deleteProduct } from '../services/productService';
import { getSectorsByStore } from '../services/sectorService';
import { ApiErrorResponse } from '../services/authService';
import styles from './ProductsPage.module.css';
import tableStyles from '../styles/shared/DataTable.module.css';
import cardGridStyles from '../styles/shared/CardGrid.module.css';
import layoutStyles from '../styles/layout.module.css';
import placeholderImage from '../assets/placeholder.png';

const ProductsPage: React.FC = () => {
  const { loading, store, error } = useStorePageGuard();
  const { currentUser } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = currentUser?.role?.role_name?.toLowerCase() === 'admin';
  const isManager = currentUser?.role?.role_name?.toLowerCase() === 'manager';
  const isUser = currentUser?.role?.role_name?.toLowerCase() === 'user';
  const canManageProducts = isAdmin || isManager || isUser;
  const canAddProduct = isAdmin || isManager || isUser;

  const [products, setProducts] = useState<Product[]>([]);
  const [sectors, setSectors] = useState<Sector[]>([]);
  const [selectedSectorId, setSelectedSectorId] = useState<number | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('table');
  const [alert, setAlert] = useState<{ message: string; type: 'success' | 'error'; isVisible: boolean }>({
    message: '',
    type: 'success',
    isVisible: false
  });

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);

  useEffect(() => {
    if (alert.isVisible) {
      const timer = setTimeout(() => {
        setAlert(prev => ({ ...prev, isVisible: false }));
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [alert.isVisible]);

  const showAlert = (message: string, type: 'success' | 'error') => {
    setAlert({ message, type, isVisible: true });
  };

  const hideAlert = () => {
    setAlert(prev => ({ ...prev, isVisible: false }));
  };

  useEffect(() => {
    const sectorIdParam = searchParams.get('sectorId');
    if (sectorIdParam) {
      setSelectedSectorId(parseInt(sectorIdParam, 10));
    } else {
      setSelectedSectorId(null);
    }
  }, [searchParams]);

  useEffect(() => {
    if (loading || error || !store) return;
    const fetchData = async () => {
      try {
        setIsLoading(true);
        hideAlert();
        const fetchedSectors = await getSectorsByStore(store.id);
        setSectors(fetchedSectors);
        let fetchedProducts: Product[];
        if (selectedSectorId) {
          fetchedProducts = await getProductsBySector(store.id, selectedSectorId);
        } else {
          fetchedProducts = await getProductsByStore(store.id);
        }
        const displayProducts = fetchedProducts.map(product => ({
          ...product,
          name: product.name || product.product_name,
          description: product.description || product.product_description
        }));
        setProducts(displayProducts);
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to load data.', 'error');
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [store, selectedSectorId, loading, error]);

  const handleSectorSelect = (sectorId: number | null) => {
    setSelectedSectorId(sectorId);
    const newSearchParams = new URLSearchParams(searchParams);
    if (sectorId) {
      newSearchParams.set('sectorId', sectorId.toString());
    } else {
      newSearchParams.delete('sectorId');
    }
    window.history.replaceState(null, '', `?${newSearchParams.toString()}`);
  };

  const handleAddProduct = () => {
    setEditingProduct(null);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleEditProduct = (product: Product) => {
    setEditingProduct(product);
    setIsModalOpen(true);
    hideAlert();
  };

  const handleDeleteProduct = async (productId: number) => {
    if (window.confirm('Are you sure you want to delete this product?')) {
      hideAlert();
      try {
        const response = await deleteProduct(productId);
        setProducts(products.filter(p => p.id !== productId));
        showAlert(response.message || 'Product deleted successfully!', 'success');
      } catch (err) {
        const apiError = err as ApiErrorResponse;
        showAlert(apiError.message || 'Failed to delete product.', 'error');
      }
    }
  };

  const handleFormSubmit = async (formData: CreateProductPayload | UpdateProductPayload, imageFile?: File) => {
    if (!store) return;
    hideAlert();
    setIsSubmitting(true);
    try {
      let savedProduct: Product;
      
      if (editingProduct) {
        const response = await updateProduct(editingProduct.id, formData as UpdateProductPayload, imageFile, store.id);
        savedProduct = response.product;
      } else {
        const response = await createProduct(store.id, formData as CreateProductPayload, imageFile);
        savedProduct = response.product;
      }

      const displayProduct = {
        ...savedProduct,
        name: savedProduct.name || savedProduct.product_name,
        description: savedProduct.description || savedProduct.product_description
      };

      if (editingProduct) {
        setProducts(products.map(p => p.id === displayProduct.id ? displayProduct : p));
        showAlert('Product updated successfully!', 'success');
      } else {
        setProducts([displayProduct, ...products]);
        showAlert('Product created successfully!', 'success');
      }
      setIsModalOpen(false);
      setEditingProduct(null);
    } catch (err) {
      const apiError = err as ApiErrorResponse;
      showAlert(apiError.message || (editingProduct ? 'Failed to update product.' : 'Failed to create product.'), 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const renderTableView = () => (
    <table className={tableStyles.dataTable}>
      <thead>
        <tr>
          <th>Image</th>
          <th>Name</th>
          <th>Description</th>
          <th>Price</th>
          <th>Sector</th>
          <th>Actions</th>
        </tr>
      </thead>
      <tbody>
        {products.map((product) => (
          <tr key={product.id}>
            <td className={tableStyles.imageCell}>
              {product.image_url ? (
                <img 
                  src={product.image_url} 
                  alt={product.name || product.product_name}
                  className={tableStyles.tableImage}
                  onError={(e) => {
                    e.currentTarget.src = placeholderImage;
                  }}
                />
              ) : (
                <div className={tableStyles.tablePlaceholder}>📦</div>
              )}
            </td>
            <td>{product.name || product.product_name}</td>
            <td>{product.description || product.product_description}</td>
            <td>{product.price ? `$${product.price.toFixed(2)}` : '-'}</td>
            <td>{product.sector?.sector_name || '-'}</td>
            <td>
              {canManageProducts && (
                <>
                  <button onClick={() => handleEditProduct(product)} className={`${tableStyles.actionButton} ${tableStyles.editButton}`}>Edit</button>
                  <button onClick={() => handleDeleteProduct(product.id)} className={`${tableStyles.actionButton} ${tableStyles.deleteButton}`}>Delete</button>
                </>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );

  const renderCardView = () => (
    <div className={cardGridStyles.cardsGrid}>
      {products.map((product) => (
        <ProductCard
          key={product.id}
          product={product}
          onEdit={canManageProducts ? handleEditProduct : () => {}}
          onDelete={canManageProducts ? handleDeleteProduct : () => {}}
          canEdit={canManageProducts}
        />
      ))}
    </div>
  );

  if (loading) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading store information...</p>
          </main>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <div className={styles.titleBar}>
              <h2 className={styles.title}>Products</h2>
            </div>
            <p className={styles.errorMessage}>{error}</p>
          </main>
        </div>
      </div>
    );
  }

  if (isLoading && products.length === 0) {
    return (
      <div className={layoutStyles.pageContainer}>
        <Header />
        <div className={styles.mainLayout}>
          <SideMenu />
          <main className={styles.contentArea}>
            <p>Loading products...</p>
          </main>
        </div>
      </div>
    );
  }

  return (
    <div className={layoutStyles.pageContainer}>
      <Header />
      <div className={styles.mainLayout}>
        <SideMenu />
        <main className={styles.contentArea}>
          <Alert 
            message={alert.message}
            type={alert.type}
            isVisible={alert.isVisible}
            onClose={hideAlert}
          />
          
          <div className={styles.titleBar}>
            <h2 className={styles.title}>Products</h2>
            <div className={styles.controls}>
              <ViewToggle currentView={viewMode} onViewChange={setViewMode} />
              {canAddProduct && (
                <button onClick={handleAddProduct} className={styles.addButton}>Add Product</button>
              )}
            </div>
          </div>

          {sectors.length > 0 && (
            <SectorFilter
              sectors={sectors}
              selectedSectorId={selectedSectorId}
              onSectorSelect={handleSectorSelect}
            />
          )}

          {products.length === 0 && !isLoading && !alert.isVisible && (
            <p>
              {selectedSectorId 
                ? `No products found in this sector. Click "Add Product" to create one.`
                : 'No products found. Click "Add Product" to create one.'
              }
            </p>
          )}

          {products.length > 0 && (
            viewMode === 'table' ? renderTableView() : renderCardView()
          )}
          
          <ProductFormModal
            isOpen={isModalOpen}
            onClose={() => { setIsModalOpen(false); setEditingProduct(null); hideAlert(); }}
            onSubmit={handleFormSubmit}
            initialData={editingProduct}
            isLoading={isSubmitting}
            selectedSectorId={selectedSectorId}
            storeId={store?.id}
          />
        </main>
      </div>
    </div>
  );
};

export default ProductsPage; 